import React from 'react'
import { FileText, Calendar, Copy, Loader2, Star } from 'lucide-react'
import type { DiagramListRow } from '@/types/database.types'

interface DiagramCardProps {
  diagram: DiagramListRow
  onOpen: (id: string) => void
  onClone: (e: React.MouseEvent, id: string) => void
  isCloning: boolean
  showUser?: boolean
}

export const DiagramCard: React.FC<DiagramCardProps> = ({
  diagram,
  onOpen,
  onClone,
  isCloning,
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`

    return date.toLocaleDateString()
  }

  return (
    <div
      onClick={() => onOpen(diagram.id)}
      className="group relative cursor-pointer overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-all hover:shadow-lg dark:border-gray-700 dark:bg-gray-800"
    >
      {/* Thumbnail */}
      <div className="aspect-video bg-gray-100 dark:bg-gray-700">
        {diagram.thumbnail ? (
          <img
            src={diagram.thumbnail}
            alt={diagram.title}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <FileText className="h-12 w-12 text-gray-400" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="truncate text-lg font-semibold text-gray-900 group-hover:text-blue-600 dark:text-white dark:group-hover:text-blue-400">
          {diagram.title}
        </h3>
        {diagram.description && (
          <p className="mt-1 line-clamp-2 text-sm text-gray-600 dark:text-gray-400">
            {diagram.description}
          </p>
        )}

        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-500">
            <Calendar className="h-3.5 w-3.5" />
            <span>Updated {formatDate(diagram.updated_at)}</span>
          </div>

          {/* Clone Button */}
          <button
            onClick={(e) => onClone(e, diagram.id)}
            disabled={isCloning}
            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-blue-700 disabled:opacity-50 dark:bg-blue-500 dark:hover:bg-blue-600"
            title="Clone to your diagrams"
          >
            {isCloning ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
            <span>Clone</span>
          </button>
        </div>
      </div>

      {/* Template Badge */}
      {diagram.is_template ? (
        <div className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-yellow-100 px-2.5 py-1 text-xs font-medium text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
          <Star className="h-3 w-3" />
          Template
        </div>
      ) : (
        <div className="absolute right-3 top-3 rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
          Public
        </div>
      )}
    </div>
  )
}
